<template>
  <v-card>
    <v-card-title> {{ model.Id == "" ? "Add Career History" : "Edit Career History" }}</v-card-title>
    <v-card-text>
      <div id="careerHistoryRegisterTab">
        <v-form ref="frmRegister" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12"
              ><InfoBox
                title="Add Your Career History here! Include your previous relevant experience, and your current role (if currently employed)"
            /></v-col>
          </v-row>
          <v-row>
            <v-col md="4" cols="12">
              <AutoCompleteField
                :readonly="isHiringmanager"
                dense
                label="Role"
                placeholder="Select Role ..."
                item-title="Name"
                item-value="id"
                :rules="[validations.required, validations.arrayMinLength(1)]"
                showRequired
                :items="jobRoles"
                v-model="selectedJobRole"
                outlined
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="f-body-1">Job Title</span>
            </v-col>
            <v-col md="4" cols="12">
              <ComboBoxField
                :readonly="isHiringmanager && !selectedJobRole"
                id="jobTitle"
                clearable
                placeholder="Select Job Title ..."
                :items="jobTitles"
                outlined
                dense
                item-title="Name"
                item-value="id"
                :rules="[validations.required]"
                closable-chips
                v-model="selectedJobTitle"
                :returnObject="false"
              >
              </ComboBoxField>
            </v-col>
            <v-col md="4" cols="12">
              <AutoCompleteField
                :readonly="isHiringmanager"
                dense
                placeholder="Employment Type"
                :rules="[validations.required]"
                showRequired
                :items="employmentTypes"
                v-model="model.EmploymentType"
                outlined
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="f-body-1">Company Details</span>
            </v-col>
            <v-col md="4" cols="12">
              <TextField
                :readonly="isHiringmanager"
                dense
                placeholder="Company Name"
                :rules="[validations.required]"
                showRequired
                outlined
                v-model="model.CompanyName"
              />
            </v-col>
            <v-col md="4" cols="12">
              <AutoCompleteField
                :readonly="isHiringmanager"
                outlined
                dense
                :rules="[validations.required]"
                showRequired
                :items="countries"
                :disable="isCountryLoading"
                :loading="isCountryLoading"
                item-title="Name"
                item-value="Name"
                placeholder="Location"
                v-model="model.Location"
              />
            </v-col>
            <v-col md="4" cols="12">
              <AutoCompleteField
                :readonly="isHiringmanager"
                dense
                :rules="[validations.required]"
                showRequired
                placeholder="On Site/Remote"
                v-model="model.LocationType"
                :items="locationTypes"
                outlined
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="f-body-1">Dates</span>
            </v-col>
            <v-col md="4" cols="12">
              <DateInputField
                :readonly="isHiringmanager"
                :rules="[validations.required]"
                first-day-of-week="1"
                v-model="model.StartDate"
                hide-actions
                placeholder="Start date"
                prepend-icon=""
                :max="new Date()"
              />
            </v-col>
            <v-col md="4" cols="12">
              <DateInputField
                :readonly="isHiringmanager"
                :rules="[validateEndDate]"
                :disabled="model.StillWorking"
                first-day-of-week="1"
                v-model="model.EndDate"
                hide-actions
                placeholder="End date"
                prepend-icon=""
              />
            </v-col>
            <v-col cols="12" md="4"><v-switch :readonly="isHiringmanager" class="mt-1" v-model="model.StillWorking" label="Still Working" /></v-col>
          </v-row>
          <v-row>
            <v-col md="4" class="12">
              <ComboBoxField
                id="skillCombo"
                clearable
                placeholder="Select Skills Relevant to the Role ..."
                label="Skills"
                :items="teammemberSkillList"
                :readonly="isHiringmanager"
                outlined
                dense
                multiple
                chips
                item-title="Name"
                item-value="id"
                :rules="[validations.required]"
                closable-chips
                v-model="selectedSkills"
              />
            </v-col>
          </v-row>
          <v-row
            ><v-col md="12">
              <TextAreaField :readonly="isHiringmanager" v-model="model.Description" outlined label="Description" placeholder="Description" /> </v-col
          ></v-row>
        </v-form>
      </div>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn v-if="!isHiringmanager" class="secondary_btn" @click="cancel()" :loading="loading" prepend-icon="mdi-cancel"> Cancel</v-btn>
      <v-btn v-if="!isHiringmanager" class="primary_btn" @click="save()" :loading="loading" prepend-icon="mdi-check">{{
        model.Id == "" ? "Add Career History" : "Save Career History"
      }}</v-btn>
    </v-card-actions>
  </v-card>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import CareerHistories from "../../../models/CareerHistories";
import { TeammemberSkill } from "../../../models/Teammember";
import { Profile } from "../../../definitions/config";
import CoreSkill from "../../../models/CoreSkill";
import validations from "../../../utils/validations";
import Details from "../../../models/Details";
import AutoCompleteField from "../../Fields/AutoCompleteField.vue";
import TextField from "../../Fields/TextField.vue";
import ComboBoxField from "../../Fields/ComboBoxField.vue";
import TextAreaField from "../../Fields/TextAreaField.vue";
import DateInputField from "../../Fields/DateInputField.vue";
import { CountryApi, CountryModel } from "../../../services/openApi/api";
import InfoBox from "../../InfoBox.vue";

export default defineComponent({
  props: [
    "editingItem",
    "skills",
    "teammemberSkills",
    "isAfterSignUp",
    "sendReqeustLoading",
    "isGettingCareerHistory",
    "details",
    "submitting",
    "resetRegisterForm",
    "isHiringmanager",
  ],
  components: {
    AutoCompleteField,
    TextField,
    ComboBoxField,
    TextAreaField,
    DateInputField,
    InfoBox,
  },
  data() {
    return {
      validations,
      shouldRequest: true,
      employmentTypes: Profile.Members.CareerHistoryEmploymentType,
      locationTypes: Profile.Members.LocationTypes,
      filteredJobTitles: [] as string[],
      endDatePickerMenu: false,
      jobRoles: [] as Details[],
      jobTitles: [] as Details[],
      startDatePickerMenu: false,
      selectedJobRole: null as string | null,
      selectedSkills: null as any[] | null,
      requestLoading: false,
      isCountryLoading: false,
      countries: [] as CountryModel[],
      loading: false,
      selectedJobTitle: null as string | null,
      valid: true,
      startMenu: false,
      endMenu: false,
      isGettingAll: false,
      model: {
        Id: "",
        StartDate: null as Date | null,
        CompanyName: "",
        Description: "",
        EmploymentType: null,
        EndDate: null as Date | null,
        JobSkills: null as string[] | null,
        JobRole: "",
        JobTitle: "",
        Location: null,
        LocationType: null,
        StillWorking: false,
      } as CareerHistories,
      technologyList: [] as string[],
    };
  },
  methods: {
    filterJobTitleByRole() {
      this.jobTitles = this.details.filter((item: any) => item.Type == "JobTitle" && item.RoleId === this.selectedJobRole);
    },
    sendRequest() {
      this.$emit("sendJobRequest");
    },
    validateEndDate(value: string): boolean | string {
      return this.model.StillWorking || !!value || "Required.";
    },
    cancel(): void {
      this.$emit("cancel");
    },
    getSkills() {
      var mappedSkills = this.selectedSkills
        ?.filter((s) => s)
        .map((c) => {
          if (c.Name) {
            return c.id;
          } else if (c) {
            return c;
          }
        }) as string[];
      var skillEl = document.getElementById("skillCombo") as any;
      var newSkill = skillEl._value;
      mappedSkills?.push(newSkill);
      this.model.JobSkills = mappedSkills;
    },
    async save(): Promise<void> {
      this.getSkills();
      const isValid = (await (this.$refs.frmRegister as any).validate()).valid;
      if (isValid) {
        if (this.selectedJobRole) {
          this.model.JobRole = this.selectedJobRole;
        }
        if (this.selectedJobTitle) {
          this.model.JobTitle = this.selectedJobTitle;
        }
        this.model.JobSkills = this.model.JobSkills.filter((c) => c);
        this.$emit("saved", {
          model: this.model,
          callbackMethod: () => {
            this.resetForm();
            this.cancel();
          },
        });
      } else if (!this.isGettingAll) {
        var el = document.querySelector("#careerHistoryRegisterTab .v-messages.error--text:first-of-type");
        this.$emit("scrollToElement", el);
      } else if (this.isGettingAll) {
        this.$emit("saved");
      }
    },
    resetForm() {
      (this.model as any) = {
        Id: "",
        CompanyName: "",
        Description: "",
        EmploymentType: "",
        EndDate: null,
        JobSkills: [] as string[],
        JobRole: "",
        JobTitle: "",
        Location: "",
        LocationType: "",
        StillWorking: false,
        StartDate: null,
      };
      this.selectedJobRole = "";
      this.selectedJobTitle = "";
      (this.$refs.frmRegister as any).reset();
    },
    getSkillName(skillId: any) {
      var skillList = [...this.skills] as CoreSkill[];
      var skillItem = skillList.find((c) => c.id === skillId);
      if (skillItem) {
        return skillItem.Text;
      }
    },
    loadJobRoleAndJobTitle() {
      this.jobRoles = this.details.filter((item: any) => item.Type == "JobRole");
      if (this.selectedJobRole) {
        this.jobTitles = this.details.filter((item: any) => item.Type == "JobTitle" && item.RoleId === this.selectedJobRole);
        if (this.selectedJobTitle) {
          this.jobTitles = this.details.filter(
            (item: any) => item.Type == "JobTitle" && item.RoleId === this.selectedJobRole && item.id === this.selectedJobTitle
          );
        }
      }
    },
  },
  async mounted() {
    if (this.editingItem && this.editingItem.Id) {
      this.model = { ...this.editingItem };
      if (this.model.JobRole) {
        this.selectedJobRole = this.model.JobRole;
        this.filterJobTitleByRole();
      }
      if (this.model.JobTitle) {
        var findJobTitle = this.jobTitles.find((c) => c.id === this.model.JobTitle);
        if (findJobTitle) {
          this.selectedJobTitle = findJobTitle.id;
        }
      }
      if (this.model.JobSkills && this.model.JobSkills.length > 0) {
        this.selectedSkills = this.skills
          .filter((c: any) => this.model.JobSkills.includes(c.id))
          .map((c: any) => {
            return { id: c.id, Name: c.Text };
          });
      }
    }
    this.loadJobRoleAndJobTitle();
    this.isCountryLoading = true;
    this.countries = (await new CountryApi().getCountries()).data;
    this.isCountryLoading = false;
  },
  computed: {
    teammemberSkillList() {
      var tmSkills = [...this.teammemberSkills] as TeammemberSkill[];
      var mappedSelectedSkills = tmSkills.map((c: any) => {
        var findSkill = this.skills.find((s: any) => s.id === c.SkillId);
        if (findSkill) {
          return { Name: findSkill.Text, id: c.SkillId };
        }
      });
      var tmSkillIds = tmSkills.map((c) => c.SkillId);
      var otherSkills = this.skills
        .filter((c: any) => !tmSkillIds.includes(c.id))
        .map((c: any) => {
          return { Name: c.Text, id: c.id };
        });
      mappedSelectedSkills.push(...otherSkills);
      return mappedSelectedSkills;
    },
  },
  watch: {
    details(newVal) {
      this.loadJobRoleAndJobTitle();
    },
    selectedJobRole(newVal) {
      this.model.JobRole = newVal;
      this.filterJobTitleByRole();
      this.selectedJobTitle = "";
      if (this.model.JobRole) {
        var findJobTitle = this.jobTitles.find((c) => c.id === this.model.JobTitle);
        if (findJobTitle) {
          this.selectedJobTitle = findJobTitle.id;
        }
      }
    },
    submitting(newVal) {
      this.loading = newVal;
    },
    isGettingCareerHistory(newVal) {
      this.isGettingAll = newVal;
      if (newVal) {
        this.save();
      }
    },
    sendReqeustLoading(newVal) {
      this.requestLoading = newVal;
    },
    "model.StillWorking": {
      handler() {
        if (this.model.StillWorking) {
          this.model.EndDate = null;
        }
      },
    },
  },
});
</script>
